// Local, cosmetic dialogue only: never consumes the simulation's random sequence.
import { PASSENGERS, type Fare, type PassengerKind } from '../sim/fares';
import type { DisasterKind } from '../sim/disasters';
import { Rng } from '../sim/rng';
import { PASSENGER_LINES, type PassengerEvent } from './passenger-lines';

export const REACTION = {
  gap: 8, eventGap: 22, pickupGrace: 3, displayMs: 4500,
  speeding: 26, speedingSeconds: 2,
  roughSpeed: 14, roughSteer: 0.8, roughSeconds: 1.2,
} as const;

type Rider = Pick<Fare, 'id' | 'passenger'>;
export interface ReactionFrame {
  time: number;
  dt: number;
  riding: readonly Rider[];
  playing: boolean;
  speed: number;
  steer: number;
  badDriving: boolean;
  disaster: DisasterKind | null;
}
export interface PassengerReaction { fareId: number; passenger: PassengerKind; event: PassengerEvent; text: string; }

export class PassengerReactions {
  private nextAt = 0;
  private fastFor = 0;
  private roughFor = 0;
  private riders = '';
  private lastFare: number | null = null;
  private eventAt = new Map<PassengerEvent, number>();
  private bags = new Map<string, string[]>();
  private lastLine = new Map<string, string>();
  private rng: Rng;

  constructor(seed = Math.floor(Math.random() * 0xffffffff)) { this.rng = new Rng(seed); }

  /** Give pickup/horn speech time to finish before an automatic comment. */
  defer(time: number, seconds = REACTION.pickupGrace): void { this.nextAt = Math.max(this.nextAt, time + seconds); }

  update(frame: ReactionFrame): PassengerReaction | null {
    const { time, dt, riding, playing, speed, steer, badDriving, disaster } = frame;
    const riders = riding.map(f => f.id).join(',');
    if (riders !== this.riders) {
      this.riders = riders;
      this.fastFor = 0; this.roughFor = 0;
      this.eventAt.clear();
      if (riding.length) this.defer(time);
    }
    if (!playing || !riding.length) { this.fastFor = 0; this.roughFor = 0; return null; }
    this.fastFor = Math.abs(speed) >= REACTION.speeding ? this.fastFor + dt : 0;
    this.roughFor = Math.abs(speed) >= REACTION.roughSpeed && Math.abs(steer) >= REACTION.roughSteer ? this.roughFor + dt : 0;
    if (time < this.nextAt) return null;
    // Immediate danger takes precedence. Do not blame disaster damage on the driver.
    const candidates: PassengerEvent[] = [];
    if (disaster) candidates.push(disaster);
    if (badDriving || this.roughFor >= REACTION.roughSeconds) candidates.push('badDriving');
    if (this.fastFor >= REACTION.speedingSeconds) candidates.push('speeding');
    const event = candidates.find(e => time >= (this.eventAt.get(e) ?? 0));
    if (!event) return null;
    const fare = riding.find(f => f.id !== this.lastFare) ?? riding[0];
    this.lastFare = fare.id;
    this.nextAt = time + REACTION.gap;
    this.eventAt.set(event, time + REACTION.eventGap);
    const passenger = PASSENGERS.find(p => p.id === fare.passenger)!;
    return { fareId: fare.id, passenger: fare.passenger, event, text: `${passenger.name}: ${this.line(fare.passenger, event)}` };
  }

  private line(passenger: PassengerKind, event: PassengerEvent): string {
    const key = `${passenger}:${event}`;
    let bag = this.bags.get(key);
    if (!bag?.length) {
      bag = this.rng.shuffle([...PASSENGER_LINES[passenger][event]]);
      // Also avoid a repeat across shuffle boundaries.
      if (bag.at(-1) === this.lastLine.get(key)) [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
      this.bags.set(key, bag);
    }
    const line = bag.pop()!;
    this.lastLine.set(key, line);
    return line;
  }
}
